import { CUTOUT_MODEL, IMAGE_MODELS, RESOLUTIONS, SONG_MODELS, VIDEO_MODELS, VIDEO_RESOLUTIONS, type AdminProvidersView, type Economics, type ModelCosts } from '@trendator/shared';
import { useEffect, useState } from 'react';
import { Link } from '../components/Layout';
import { Button, Card, CardTitle, Dot, Empty, ErrorBox, Field, Input, Label, Loading, PageHead, Switch, Table, useToast } from '../components/ui';
import { api, errorText } from '../lib/api';
import { num, seconds, shortId, usd, when } from '../lib/format';
import { useLoad } from '../lib/hooks';

const NAME = { kie: 'kie.ai', fal: 'fal.ai' } as const;
type P = keyof typeof NAME;

/** Строки таблицы цен: модель, провайдеры, колонки и единица. */
const COST_ROWS: { id: string; label: string; providers: P[]; cols: readonly string[]; unit: string }[] = [
  ...IMAGE_MODELS.map((m) => ({ id: m.id, label: m.label, providers: ['kie', 'fal'] as P[], cols: RESOLUTIONS, unit: 'за кадр' })),
  ...VIDEO_MODELS.map((m) => ({ id: m.id, label: m.label, providers: ['kie', 'fal'] as P[], cols: VIDEO_RESOLUTIONS, unit: 'за секунду' })),
  ...SONG_MODELS.map((m) => ({ id: m.id, label: m.label, providers: ['kie'] as P[], cols: ['track'], unit: 'за запрос' })),
  { id: CUTOUT_MODEL, label: 'Вырезание фона (стикеры)', providers: ['kie', 'fal'], cols: ['image'], unit: 'за картинку' },
  { id: 'qc-vision', label: 'Проверка брака (vision)', providers: ['kie'], cols: ['check'], unit: 'за проверку' },
];
const COL_LABEL: Record<string, string> = { track: 'запрос', check: 'проверка', image: 'картинка' };

export function Providers() {
  const d = useLoad(() => api.get<AdminProvidersView>('/providers'));
  if (d.error && !d.data) return <ErrorBox text={d.error} onRetry={() => void d.reload()} />;
  if (!d.data) return <Loading />;
  return <View v={d.data} reload={() => void d.reload()} />;
}

function View({ v, reload }: { v: AdminProvidersView; reload: () => void }) {
  const toast = useToast();
  const [settings, setSettings] = useState(v.settings);
  const [eco, setEco] = useState<Economics>(v.economics);
  const [costs, setCosts] = useState<ModelCosts>(v.modelCosts);
  const [qc, setQc] = useState(v.qc);
  const [busy, setBusy] = useState<string | null>(null);
  useEffect(() => {
    setSettings(v.settings);
    setEco(v.economics);
    setCosts(v.modelCosts);
    setQc(v.qc);
  }, [v]);

  const save = async (key: string, value: unknown, ok: string) => {
    setBusy(key);
    try {
      await api.put(`/settings/${key}`, value);
      toast(ok);
      reload();
    } catch (err) {
      toast(errorText(err), 'bad');
    } finally {
      setBusy(null);
    }
  };

  const dirty = (a: unknown, b: unknown) => JSON.stringify(a) !== JSON.stringify(b);
  const swap = () => setSettings((s) => ({ ...s, order: [...s.order].reverse() as P[] }));

  return (
    <>
      <PageHead title="Провайдеры" />
      <div className="flex flex-wrap items-start gap-4">
        <Card className="flex min-w-0 flex-[1_1_360px] flex-col gap-4">
          <CardTitle right={<Label>failover сверху вниз</Label>}>Порядок и включение</CardTitle>
          {settings.order.map((p, i) => {
            const st = v.stats.find((s) => s.provider === p);
            return (
              <div key={p} className="flex items-center gap-3 rounded-2xl bg-field p-3.5">
                <span className="flex size-8 shrink-0 items-center justify-center rounded-[10px] bg-chip text-sm font-extrabold">{i + 1}</span>
                <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span className="flex items-center gap-2 font-bold">
                    {NAME[p]} {i === 0 ? <span className="text-xs font-semibold text-accent">основной</span> : <span className="text-xs font-semibold text-muted">запасной</span>}
                  </span>
                  <span className="flex items-center gap-1.5 text-xs text-muted">
                    <Dot tone={v.configured[p] ? 'ok' : 'bad'} />
                    {v.configured[p] ? 'ключ задан' : `нет ключа (${p === 'kie' ? 'KIE_API_KEY' : 'FAL_KEY'})`}
                    {p === 'kie' && v.kieCredits !== null ? ` · ${num(Math.round(v.kieCredits))} кредитов` : ''}
                    {st ? ` · 24ч: ${st.submitted} задач` : ''}
                  </span>
                </span>
                <Switch checked={settings.enabled[p]} onChange={(on) => setSettings((s) => ({ ...s, enabled: { ...s.enabled, [p]: on } }))} label={`${NAME[p]} включён`} />
              </div>
            );
          })}
          <div className="flex gap-2">
            <Button className="flex-1" onClick={swap}>
              Поменять местами
            </Button>
            <Button variant="accent" className="flex-1" disabled={!dirty(settings, v.settings)} busy={busy === 'providers'} onClick={() => void save('providers', settings, 'Порядок сохранён')}>
              Сохранить
            </Button>
          </div>
          <Label>Воркер подхватывает настройки в течение 30 секунд. Выключенный провайдер пропускается при фолбэке.</Label>
        </Card>

        <Card className="flex min-w-0 flex-[1_1_300px] flex-col gap-4">
          <CardTitle>Экономика</CardTitle>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Курс $, ₽">
              <Input type="number" step="0.1" value={eco.usdRub} onChange={(e) => setEco({ ...eco, usdRub: Number(e.target.value) })} />
            </Field>
            <Field label="Эквайринг, %">
              <Input type="number" step="0.1" value={eco.acquiringPct} onChange={(e) => setEco({ ...eco, acquiringPct: Number(e.target.value) })} />
            </Field>
            <Field label="Запас на брак, %">
              <Input type="number" step="0.5" value={eco.defectReservePct} onChange={(e) => setEco({ ...eco, defectReservePct: Number(e.target.value) })} />
            </Field>
          </div>
          <Button variant="accent" disabled={!dirty(eco, v.economics)} busy={busy === 'economics'} onClick={() => void save('economics', eco, 'Экономика сохранена')}>
            Сохранить
          </Button>
          <Label>По этим цифрам считается себестоимость и маржа трендов и дашборда.</Label>
        </Card>
      </div>

      <Card className="flex flex-col gap-4">
        <CardTitle right={<Label>$, по прайсу провайдера</Label>}>Цены моделей</CardTitle>
        <Table>
          <thead>
            <tr>
              <th>Модель</th>
              <th>Провайдер</th>
              <th>Единица</th>
              <th colSpan={3}>Цена</th>
            </tr>
          </thead>
          <tbody>
            {COST_ROWS.flatMap((m) =>
              m.providers.map((p) => (
                <tr key={`${m.id}-${p}`}>
                  <td className="font-semibold">{m.label}</td>
                  <td className="text-soft">{NAME[p]}</td>
                  <td className="text-soft">{m.unit}</td>
                  {m.cols.map((r) => (
                    <td key={r} colSpan={m.cols.length === 1 ? 3 : 1}>
                      <label className="flex items-center gap-2">
                        <span className="w-12 text-xs text-muted">{COL_LABEL[r] ?? r}</span>
                        <Input
                          type="number"
                          step="0.001"
                          min={0}
                          aria-label={`${m.label} ${NAME[p]} ${COL_LABEL[r] ?? r}`}
                          className="h-9 w-24 text-sm"
                          value={costs[m.id]?.[p]?.[r] ?? ''}
                          onChange={(e) =>
                            setCosts((c) => ({ ...c, [m.id]: { ...c[m.id], [p]: { ...c[m.id]?.[p], [r]: e.target.value === '' ? undefined : Number(e.target.value) } } }))
                          }
                        />
                      </label>
                    </td>
                  ))}
                </tr>
              )),
            )}
          </tbody>
        </Table>
        <Button variant="accent" className="self-start" disabled={!dirty(costs, v.modelCosts)} busy={busy === 'model_costs'} onClick={() => void save('model_costs', costs, 'Цены моделей сохранены')}>
          Сохранить цены
        </Button>
        <Label>Песни и проверку брака fal.ai не умеет — идут только через kie.ai.</Label>
      </Card>

      <Card className="flex flex-col gap-4">
        <CardTitle>Контроль качества</CardTitle>
        <div className="flex items-center justify-between gap-3">
          <span className="flex flex-col">
            <span className="text-sm font-semibold">Проверять кадры с шагом «Контроль»</span>
            <span className="text-xs text-muted">сходство лица + брак глазами модели, брак переделываем за наш счёт</span>
          </span>
          <Switch checked={qc.enabled} onChange={(on) => setQc({ enabled: on })} label="Контроль качества включён" />
        </div>
        <span className="flex items-center gap-1.5 text-sm">
          <Dot tone={v.faceService ? 'ok' : 'bad'} />
          {v.faceService ? 'Сервис сверки лиц подключён' : 'Сервис сверки лиц не настроен (FACE_SERVICE_URL) — двойник недоступен, сверка лица пропускается'}
        </span>
        <Button variant="accent" className="self-start" disabled={!dirty(qc, v.qc)} busy={busy === 'qc'} onClick={() => void save('qc', qc, 'Контроль качества сохранён')}>
          Сохранить
        </Button>
        <Label>Выключенный контроль пропускает проверку — удобно, если vision-модель тормозит или дорожает.</Label>
      </Card>

      <div className="flex flex-wrap items-start gap-4">
        <Card className="flex min-w-0 flex-[1_1_420px] flex-col gap-3">
          <CardTitle right={<Label>последние 24 часа</Label>}>Работа</CardTitle>
          <Table>
            <thead>
              <tr>
                <th>Провайдер</th>
                <th className="text-right!">Задач</th>
                <th className="text-right!">Готово</th>
                <th className="text-right!">Ошибок</th>
                <th className="text-right!">Ушло на запасной</th>
                <th className="text-right!">Среднее время</th>
                <th className="text-right!">Потрачено</th>
              </tr>
            </thead>
            <tbody>
              {v.stats.map((s) => (
                <tr key={s.provider}>
                  <td className="font-semibold">{NAME[s.provider as P] ?? s.provider}</td>
                  <td className="text-right">{num(s.submitted)}</td>
                  <td className="text-right text-ok">{num(s.succeeded)}</td>
                  <td className={s.failed ? 'text-right text-bad' : 'text-right'}>{num(s.failed)}</td>
                  <td className="text-right">{num(s.fellBack)}</td>
                  <td className="text-right">{seconds(s.avgSec)}</td>
                  <td className="text-right">{usd(Math.round(s.costUsd * 100) / 100)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
        <Card className="flex min-w-0 flex-[1_1_360px] flex-col gap-3">
          <CardTitle>Последние ошибки</CardTitle>
          {v.errors.length === 0 ? <Empty>Ошибок нет</Empty> : null}
          {v.errors.map((e) => (
            <Link key={e.taskId} to={`/generations?id=${e.generationId}`} className="flex flex-col gap-0.5 rounded-xl bg-field px-3 py-2.5 text-white hover:bg-white/6">
              <span className="flex justify-between gap-2 text-xs text-muted">
                <span>
                  {e.provider ? NAME[e.provider as P] : 'до отправки'} · #{shortId(e.generationId)}
                </span>
                <span>{when(e.createdAt)}</span>
              </span>
              <span className="line-clamp-2 text-[13px] break-words">{e.error}</span>
            </Link>
          ))}
        </Card>
      </div>
    </>
  );
}
